<template>
  <div class="page-hero" :class="`page-hero--${variant}`">
    <div class="page-hero__bg"/>
    <div class="page-hero__content">
      <div>
        <h3 class="page-hero__title mb-25">{{ title }}</h3>
        <p class="page-hero__subtitle mb-0">{{ subtitle }}</p>
      </div>
      <div v-if="stats && stats.length" class="d-flex flex-wrap mt-1" style="gap:10px">
        <div v-for="(s, i) in stats" :key="i"
             class="page-hero__stat"
             :class="s.variant ? `page-hero__stat--${s.variant}` : ''">
          <feather-icon v-if="s.icon" :icon="s.icon" size="14"/>
          <span v-if="s.value!=null" class="font-weight-bolder">{{ s.value }}</span>
          {{ s.label }}
        </div>
      </div>
    </div>
    <div class="page-hero__illustration">
      <slot name="illustration"/>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PageHero',
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    stats: { type: Array, default: () => [] },
    variant: { type: String, default: 'primary' }
  }
}
</script>

<style scoped>
.page-hero {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  min-height: 110px;
}
.page-hero__bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}
.page-hero__content {
  position: relative;
  z-index: 1;
  padding: 18px 24px;
}
.page-hero__title { color: #fff; font-weight: 700; }
.page-hero__subtitle { color: rgba(255,255,255,.8); font-size: .85rem; }
.page-hero__stat {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: .78rem;
  color: #fff;
  background: rgba(255,255,255,.2);
}
.page-hero__stat--success { background: rgba(40,199,111,.4); }
.page-hero__stat--warning { background: rgba(255,159,67,.35); }
.page-hero__stat--secondary { background: rgba(255,255,255,.15); }
.page-hero__illustration {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  width: 90px;
  height: auto;
  opacity: .6;
  z-index: 0;
  pointer-events: none;
}
.page-hero__illustration ::v-deep svg { width: 100%; height: auto; }
@media (max-width: 576px) {
  .page-hero__illustration { display: none; }
  .page-hero { min-height: auto; }
}

/* Variants */
.page-hero--primary .page-hero__bg {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 50%, #ce9ffc 100%);
}
.page-hero--contract .page-hero__bg {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 50%, #b8a9ff 100%);
}
.page-hero--client .page-hero__bg {
  background: linear-gradient(135deg, #00cfe8 0%, #1eacf7 50%, #7367f0 100%);
}
.page-hero--realestate .page-hero__bg {
  background: linear-gradient(135deg, #ff9f43 0%, #feca57 50%, #ff9f43 100%);
}
.page-hero--building .page-hero__bg {
  background: linear-gradient(135deg, #28c76f 0%, #00d4b5 50%, #00cfe8 100%);
}
.page-hero--user .page-hero__bg {
  background: linear-gradient(135deg, #5e5873 0%, #b9b9c3 50%, #7367f0 100%);
}
.page-hero--branch .page-hero__bg {
  background: linear-gradient(135deg, #28c76f 0%, #48da89 50%, #00cfe8 100%);
}
.page-hero--organization .page-hero__bg {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 50%, #b8a9ff 100%);
}
.page-hero--deleted .page-hero__bg {
  background: linear-gradient(135deg, #82868b 0%, #a8aaae 50%, #b9b9c3 100%);
}
.page-hero--keybox .page-hero__bg {
  background: linear-gradient(135deg, #00cfe8 0%, #28c76f 50%, #00d4b5 100%);
}
</style>

<style>
.dark-layout .page-hero--primary .page-hero__bg,
.dark-layout .page-hero--contract .page-hero__bg,
.dark-layout .page-hero--organization .page-hero__bg {
  background: linear-gradient(135deg, #4839a0 0%, #7367f0 50%, #9e84e9 100%);
}
.dark-layout .page-hero--client .page-hero__bg {
  background: linear-gradient(135deg, #008a9a 0%, #1eacf7 50%, #7367f0 100%);
}
.dark-layout .page-hero--realestate .page-hero__bg {
  background: linear-gradient(135deg, #c77a1a 0%, #e6a23c 50%, #c77a1a 100%);
}
.dark-layout .page-hero--building .page-hero__bg,
.dark-layout .page-hero--branch .page-hero__bg {
  background: linear-gradient(135deg, #1a9d5c 0%, #00a892 50%, #008a9a 100%);
}
.dark-layout .page-hero--user .page-hero__bg {
  background: linear-gradient(135deg, #4b4651 0%, #7367f0 50%, #6e7b8a 100%);
}
.dark-layout .page-hero--deleted .page-hero__bg {
  background: linear-gradient(135deg, #5a5d63 0%, #6e7b8a 50%, #5a5d63 100%);
}
.dark-layout .page-hero--keybox .page-hero__bg {
  background: linear-gradient(135deg, #008a9a 0%, #1a9d5c 50%, #00a892 100%);
}
</style>
